import { TRACKCODE_LOGISTIC_COMPANY_ID } from "common/trackcodeInternal";
import { getActiveLogisticCompanyName } from "features/app/components/logisticCompanySwitcherUtils";

const OWNER_PROFILES = {
  manuel: {
    name: "Manuel",
    fullName: "Manuel von TrackCode",
    initials: "MW"
  },
  florian: {
    name: "Florian",
    fullName: "Florian von TrackCode",
    initials: "FH"
  }
};

const ARTIFACT_TYPE_LABELS = {
  "termin-zusammenfassung": "Termin-Zusammenfassung",
  notiz: "Notiz",
  recherche: "Recherche",
  anruf: "Anruf",
  email: "E-Mail",
  projekt: "Projekt"
};

const ARTIFACT_STATUS_LABELS = {
  neu: "Neu",
  draft: "Entwurf",
  review: "Review",
  published: "Veröffentlicht"
};

export const formatWorkspaceTitle = workspace =>
  `${workspace.title} × ${workspace.partner}`;

export const formatCollaborationHeaderTitle = ({
  managedLogisticCompanies,
  logisticCompanyId,
  user,
  partner = "TrackCode"
}) => ({
  customerName: getActiveLogisticCompanyName({
    managedLogisticCompanies,
    logisticCompanyId,
    user
  }),
  partner
});

export const formatCollaborationHeaderLabel = ({
  managedLogisticCompanies,
  logisticCompanyId,
  user,
  partner = "TrackCode"
}) => {
  const { customerName, partner: partnerName } = formatCollaborationHeaderTitle({
    managedLogisticCompanies,
    logisticCompanyId,
    user,
    partner
  });

  return `${customerName} × ${partnerName}`;
};

export const resolveCollaborationEmptyState = ({
  isInternalUser,
  activeLogisticCompanyId
}) => {
  if (
    isInternalUser &&
    activeLogisticCompanyId === TRACKCODE_LOGISTIC_COMPANY_ID
  ) {
    return {
      title: "Mandant wählen",
      message:
        "Wählen Sie über das Menü links den Kundenmandanten, dessen Workspace Sie öffnen möchten."
    };
  }

  return {
    title: "Noch kein Workspace eingerichtet",
    message:
      "Für Ihren Mandanten wurde noch kein Zusammenarbeits-Workspace angelegt. Bitte wenden Sie sich an Ihren TrackCode-Ansprechpartner."
  };
};

export const getCurrentJourneyLabel = journey => {
  if (!journey) {
    return "—";
  }

  const currentStage = journey.stages?.find(stage => stage.id === journey.current);
  return currentStage?.label || journey.current || "—";
};

export const getCurrentJourneyStatusLabel = journey => {
  if (!journey?.stages?.length) {
    return "—";
  }

  const currentStage =
    journey.stages.find(stage => stage.status === "active") ||
    journey.stages.find(stage => stage.id === journey.current);

  if (!currentStage) {
    return getCurrentJourneyLabel(journey);
  }

  if (currentStage.meta) {
    return `${currentStage.label} ${currentStage.meta}`.trim();
  }

  return currentStage.label;
};

export const formatJourneyStageMeta = stage => {
  if (
    stage?.date &&
    (stage.status === "done" || stage.status === "active")
  ) {
    return formatArtifactDate(stage.date);
  }
  return stage?.meta || "\u00a0";
};

export const formatCollaborationDate = value => {
  if (!value) {
    return "—";
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleDateString("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
};

export const formatArtifactDate = value => {
  if (!value || value === "1000-01-01" || String(value).startsWith("1000-01-01")) {
    return "Datum offen";
  }

  const parts = String(value).split("-");
  if (parts.length === 3) {
    const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString("de-DE", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });
    }
  }

  return formatCollaborationDate(value);
};

export const formatArtifactType = type =>
  ARTIFACT_TYPE_LABELS[type] || type || "—";

export const formatArtifactStatus = status =>
  ARTIFACT_STATUS_LABELS[status] || status || "—";

export const getOwnerProfile = owner => {
  const key = String(owner || "").toLowerCase();
  return (
    OWNER_PROFILES[key] || {
      name: owner || "—",
      fullName: owner || "—",
      initials: (owner || "?").slice(0, 2).toUpperCase()
    }
  );
};

export const formatRelativeTime = value => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) {
    return "gerade eben";
  }
  if (diffMinutes < 60) {
    return `vor ${diffMinutes} Minute${diffMinutes === 1 ? "" : "n"}`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `vor ${diffHours} Stunde${diffHours === 1 ? "" : "n"}`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return `vor ${diffDays} Tag${diffDays === 1 ? "" : "en"}`;
  }

  return formatCollaborationDate(date);
};

export const resolveHeroArtifact = (artifacts, { isInternalUser }) => {
  if (!artifacts?.length) {
    return null;
  }

  const visible = isInternalUser
    ? artifacts
    : artifacts.filter(
        artifact => artifact.visibility === "shared" || !artifact.visibility
      );

  const featured = visible.find(artifact => artifact.featured);
  if (featured) {
    return featured;
  }

  return visible[0] || null;
};

export const resolveHistoryArtifacts = (artifacts, heroArtifact) => {
  if (!artifacts?.length) {
    return [];
  }

  const heroKey = heroArtifact?.key;
  return artifacts.filter(artifact => artifact.key !== heroKey);
};

export const sortArtifacts = (artifacts, sortBy, direction = "desc") => {
  const sorted = [...artifacts];

  sorted.sort((left, right) => {
    let comparison = 0;

    if (sortBy === "title") {
      comparison = left.title.localeCompare(right.title, "de");
    } else if (sortBy === "owner") {
      comparison = getOwnerProfile(left.owner).name.localeCompare(
        getOwnerProfile(right.owner).name,
        "de"
      );
    } else if (sortBy === "shared") {
      const leftShared = left.visibility === "shared" ? 1 : 0;
      const rightShared = right.visibility === "shared" ? 1 : 0;
      comparison = leftShared - rightShared;
    } else {
      const leftDate = new Date(left.sortDate || left.date).getTime();
      const rightDate = new Date(right.sortDate || right.date).getTime();
      comparison = leftDate - rightDate;
    }

    return direction === "asc" ? comparison : -comparison;
  });

  return sorted;
};

/**
 * Resolves how /collaboration should render for the current user.
 * @returns {{ type: "redirect", slug: string } | { type: "empty" } | { type: "listing" }}
 */
export const resolveCollaborationIndexView = ({ isInternalUser, workspaces }) => {
  if (workspaces.length === 0) {
    return { type: "empty" };
  }

  if (isInternalUser || workspaces.length === 1) {
    return { type: "redirect", slug: workspaces[0].slug };
  }

  return { type: "listing" };
};
